import {
  get,
  isUndefined,
  reduce,
  last,
  each,
  isObject,
  indexOf,
  cloneDeep,
} from 'lodash-es';
import debug from 'debug';
import { OpenAPIV3 } from 'openapi-types';

const log = debug('itp:core:openapi-tools');

export function getReference<T = any>(doc: OpenAPIV3.Document, path: string) {
  if (!path.startsWith('#')) {
    throw new Error(
      `Invalid $ref path. It must start with "#" character. path=${path}`,
    );
  }

  let result = get(doc, path.split('/').slice(1));

  if (!result) {
    throw new Error(`$ref not found: ${path}`);
  }

  return result as T;
}

export function valueAs<T>(doc: OpenAPIV3.Document, data: T | object): T {
  if (get(data, '$ref', null) !== null) {
    return getReference<T>(doc, (data as OpenAPIV3.ReferenceObject).$ref);
  }

  return data as T;
}

export function defaultValue(
  doc: OpenAPIV3.Document,
  obj: OpenAPIV3.SchemaObject,
): any {
  if (!isUndefined(obj.default)) {
    return obj.default;
  }

  if (obj.type === 'object') {
    return defaultsFromSchema(doc, obj);
  }

  if (obj.type === 'array') {
    return [];
  }

  return null;
}

function defaultsFromSchema(
  doc: OpenAPIV3.Document,
  schema: OpenAPIV3.SchemaObject,
) {
  return reduce(
    schema.properties,
    (m, v, k) => (
      (m[k] = defaultValue(doc, valueAs<OpenAPIV3.SchemaObject>(doc, v))),
      m
    ),
    {} as Dictionary<any>,
  );
}

export const getSchemaDefaults = (
  doc: OpenAPIV3.Document,
  schema: OpenAPIV3.SchemaObject,
) => defaultsFromSchema(doc, schema);

export function getOperation(
  doc: OpenAPIV3.Document,
  operationId: string,
): OpenAPIV3.OperationObject {
  if (operationId.indexOf('/') > -1) {
    const lastToken = last(operationId.split('/'));
    if (lastToken) {
      operationId = lastToken;
    }
  }

  let result: OpenAPIV3.OperationObject | null = null;

  each(doc.paths, (pathItem, url) => {
    each(pathItem, (op, method) => {
      if (get(op, 'operationId') === operationId) {
        result = op as OpenAPIV3.OperationObject;
      }
    });
  });

  if (!result) {
    throw new Error(`invalid OpenAPI operationId: ${operationId}`);
  }

  return result;
}

export function getSchema(
  doc: OpenAPIV3.Document,
  name: string,
): OpenAPIV3.SchemaObject {
  let result;

  const key = last(name.split('/')) as string;

  if (
    isObject(doc.components) &&
    isObject(doc.components.schemas) &&
    key in doc.components.schemas
  ) {
    result = valueAs<OpenAPIV3.SchemaObject>(doc, doc.components.schemas[key]);
  }

  if (isUndefined(result)) {
    throw new Error(`OpenAPI schema not found: ${name}`);
  }

  return result;
}

export function hasProperty(
  schema: OpenAPIV3.SchemaObject,
  name: string,
): boolean {
  return !!(schema.properties && name in schema.properties);
}

function getProperty(
  doc: OpenAPIV3.Document,
  schema: OpenAPIV3.SchemaObject,
  name: string,
): OpenAPIV3.SchemaObject {
  let result;

  if (schema.properties && name in schema.properties) {
    result = valueAs<OpenAPIV3.SchemaObject>(doc, schema.properties[name]);
  }

  if (!result) {
    throw new Error(
      `OpenAPI property not found: ${name}. (schema=${
        schema.title || schema.description
      })`,
    );
  }

  return result;
}

export function getPropertyRules(
  doc: OpenAPIV3.Document,
  schema: string,
  property: string,
) {
  const sch = getSchema(doc, schema);

  if (!hasProperty(sch, property)) {
    return { readonly: true };
  }

  const prop = getProperty(doc, sch, property);
  const result: Dictionary<any> = {};

  if (prop.maximum != null) {
    result['max_value'] = prop.maximum;
  }

  if (prop.minimum != null) {
    result['min_value'] = prop.minimum;
  }

  if (prop.maxLength != null) {
    result['max'] = prop.maxLength;
  }

  if (prop.minLength != null) {
    result['min'] = prop.minLength;
  }

  if (indexOf(sch.required, property) > -1) {
    result['required'] = true;
  }

  if (prop.enum) {
    result['oneOf'] = cloneDeep(prop.enum);
  }

  return result;
}
